// src/Pages/Public/Homepage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../Components/Common/Navbar';
import DashboardImage from '../../assets/images/dashboard-image.png';
import '../../Stylesheets/Public/Homepage.css';
import FooterWithNewsletter from '../../Components/Home/FooterWithNewsletter';

import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';


/* ---------- Data ---------- */

const modules = [
  { title: 'Projects', desc: 'Plan, track, and manage sites from start to handover.', icon: '🏗️' },
  { title: 'Labor', desc: 'Attendance, payments, and worker insights in one place.', icon: '👷' },
  { title: 'Materials', desc: 'Inventory, issues/receipts, bills & usage history.', icon: '🧱' },
  { title: 'Expenses', desc: 'Log expenses with bills and export reports instantly.', icon: '💳' },

  // ✅ Coming soon features
  { title: 'Analytics', desc: 'Dashboards for cost, progress, usage & productivity.', icon: '📊', soon: true },
  { title: 'Smart Suggestions', desc: 'AI suggestions to reduce wastage and delays.', icon: '🤖', soon: true },
  { title: 'Plugins', desc: 'Add-ons like WhatsApp alerts and more integrations.', icon: '🧩', soon: true },
  { title: 'WhatsApp Notifications', desc: 'Auto updates for attendance, payments & reports.', icon: '💬', soon: true },
  { title: 'Team Roles', desc: 'Role-based access for owner, supervisor, accountant.', icon: '🛡️', soon: true }
];


const features = [
  {
    title: 'Stop losing money to chaos',
    desc: 'Know exactly what’s spent, what’s left, and what’s stuck. No Excel drama.'
  },
  {
    title: 'Mobile-first for site reality',
    desc: 'Supervisors can update logs, labor, and material usage on the go.'
  },
  {
    title: 'Reports in 1 click',
    desc: 'Export PDF/CSV for clients, billing, audits, and reviews.'
  },
  {
    title: 'Razorpay billing built-in',
    desc: 'Move from MVP to paid SaaS with subscriptions already wired.'
  }
];

const steps = [
  { num: '01', title: 'Create a project', text: 'Set budget, timeline, location, and team.' },
  { num: '02', title: 'Log daily activity', text: 'Daily logs, progress, photos, notes.' },
  { num: '03', title: 'Track labor & materials', text: 'Attendance, wages, inventory, bills.' },
  { num: '04', title: 'Export reports', text: 'Generate clean reports for clients and internal review.' }
];


const plans = [
  {
    name: 'Free',
    price: '₹0',
    tagline: 'For single-site early users',
    items: ['1 Project', 'Basic Daily Logs', 'Labor tracking', 'Material inventory'],
    cta: 'Start Free',
    popular: false
  },
  {
    name: 'Pro',
    price: '₹499/mo',
    tagline: 'For serious contractors',
    items: [
      'Unlimited Projects',
      'Advanced Reports (PDF/CSV)',
      'Analytics Dashboard (Coming Soon)',
      'Team Roles & Access (Coming Soon)',
      'WhatsApp Notifications (Coming Soon)',
      'Priority Support'
    ],
    cta: 'Go Pro',
    popular: true
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    tagline: 'For multi-site companies',
    items: [
      'Custom Integrations (Coming Soon)',
      'Dedicated Success Manager',
      'Plugin Marketplace (Coming Soon)',
      'SLA + Security Review (Coming Soon)'
    ],
    cta: 'Talk to Sales',
    popular: false
  }
];

const faqs = [
  {
    q: 'Is Site360 meant for small contractors or big firms?',
    a: 'Both. Small teams get structure fast, big firms get multi-site control and analytics.'
  },
  {
    q: 'Can I export reports for clients?',
    a: 'Yes. PDF and CSV exports are built into Expenses and Analytics.'
  },
  {
    q: 'Will this work offline on site?',
    a: 'Not in MVP. Offline sync is planned for later phases.'
  },
  {
    q: 'Is AI mandatory?',
    a: 'No. You can get value from the core product without any AI features.'
  },
  {
    q: 'Are Analytics, Team Roles, Plugins and WhatsApp alerts available in MVP?',
    a: 'Not in MVP. We are launching core tracking first (Projects, Labor, Materials, Expenses). These advanced features will roll out next.'
  }

];

const testimonials = [
  {
    role: 'Site Engineer',
    text: 'Earlier I spent 2 hours daily checking labor + material. Now it’s 10 minutes.'
  },
  {
    role: 'Contractor',
    text: 'Clients love the expense PDF. Looks professional, no arguments.'
  },
  {
    role: 'Supervisor',
    text: 'Attendance and payments are finally clean. Workers trust it.'
  }
];

/* ---------- Reveal Wrapper ---------- */

const Reveal = ({ children, className = '', delay = 0, y = 24 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.18 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut', delay }}
    >
      {children}
    </motion.div>
  );
};

/* ---------- Homepage ---------- */

const Homepage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);


  const navigate = useNavigate();
  const auth = getAuth();

  const [fbUser, setFbUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setFbUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, [auth]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ✅ MVP behavior:
  // - If logged in -> go dashboard
  // - Else -> go signup

  const goCreateAccount = () => navigate('/signup');

  const goLogin = () => navigate('/signin');
  // optional if you have it

  const goExploreModules = () => scrollToId('modules');

  const goSeePlans = () => scrollToId('pricing');

  const goRoadmap = () => scrollToId('pricing');

  const goToBilling = () => {
    if (!authReady) return; // optional: ignore clicks until ready
    if (fbUser) navigate('/dashboard/profile/billing');
    else navigate('/signup');
  };

  const goStartFree = () => {
    if (!authReady) return;
    if (fbUser) navigate('/dashboard/projects');
    else navigate('/signup');
  };


  return (
    <div className="homepage-root">
      <Navbar />

      {/* HERO */}
      <div className="lp-hero-wrapper">
        <section id="home" className="lp-hero hero-v2">
          <div className="lp-hero-bg" />

          <div className="page-container">
            <div className="hero-layout hero-layout-v2">
              {/* LEFT */}
              <motion.div
                className="hero-left"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <div className="lp-pill">Construction ops, finally organized</div>

                <h1 className="hero-title hero-title-v2">
                  Run your construction
                  <br />
                  sites like a product —
                  <br />
                  not a mess.
                </h1>

                <p className="hero-subtitle hero-subtitle-v2">
                  Site360 helps you track labor, materials, expenses, and progress in real-time. One
                  dashboard. Zero spreadsheet chaos.
                </p>

                <div className="hero-cta-row hero-cta-row-v2">
                  <button className="btn btn-primary" onClick={goStartFree}>Start Free</button>
                  <button className="btn btn-outline" onClick={goExploreModules}>Explore Modules</button>
                </div>

                <div className="hero-stats-row hero-stats-row-v2">
                  <div className="hero-stat">
                    <div className="hero-stat-number">+30%</div>
                    <div className="hero-stat-label">Less material wastage</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-number">2x</div>
                    <div className="hero-stat-label">Faster reporting</div>
                  </div>
                  <div className="hero-stat">
                    <div className="hero-stat-number">0</div>
                    <div className="hero-stat-label">Excel nightmares</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="hero-right hero-right-v2"
                initial={{ opacity: 0, scale: 0.98, y: 14 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.9, ease: 'easeOut', delay: 0.08 }}
              >
                {/* ✅ wrapper allows labels to float OUTSIDE without getting cut */}
                <div className="ui-window-wrap">
                  {/* ✅ labels OUTSIDE ui-window (so overflow hidden won't clip them) */}
                  <motion.div
                    className="ui-float ui-float-logs"
                    initial={{ opacity: 0, x: -10, y: 6 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
                  >
                    📌 <b>Daily Logs</b>
                  </motion.div>

                  <motion.div
                    className="ui-float ui-float-materials"
                    initial={{ opacity: 0, x: -10, y: 6 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.28, ease: 'easeOut' }}
                  >
                    🧱 <b>Materials</b>
                  </motion.div>

                  <motion.div
                    className="ui-float ui-float-labor"
                    initial={{ opacity: 0, x: 10, y: 6 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.32, ease: 'easeOut' }}
                  >
                    👷 <b>Labor</b>
                  </motion.div>

                  {/* ✅ window keeps overflow hidden for rounded corners */}
                  <div className="ui-window">
                    <div className="ui-window-topbar">
                      <span className="ui-dot red" />
                      <span className="ui-dot yellow" />
                      <span className="ui-dot green" />
                      <span className="ui-window-title">site360.app</span>
                    </div>

                    <div className="ui-window-body ui-window-body--shot">
                      <div className="ui-shot-wrap">
                        <img
                          src={DashboardImage}
                          alt="Site360 dashboard preview"
                          className="ui-shot"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        </section>
      </div>





      {/* TRUST STRIP */}
      <section className="trust-strip">
        <div className="page-container trust-inner">
          <p className="trust-label">BUILT FOR ON-SITE TEAMS</p>
          <div className="trust-pills">
            {['Contractors', 'Site Engineers', 'Supervisors', 'Builders', 'PMC Teams'].map(
              (item) => (
                <span key={item} className="trust-pill">
                  {item}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="lp-section">
        <div className="page-container">
          <Reveal className="section-header">
            <h2>Everything you need to run a site</h2>
            <p>
              Modules match how construction actually works — not how generic tools think it does.
            </p>
          </Reveal>

          <div className="cards-grid">
            {modules.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06}>
                <div className={`lp-card ${m.soon ? 'is-soon' : ''}`}>
                  <div className="lp-card-top">
                    <div className="lp-card-icon">{m.icon}</div>
                    {m.soon && <span className="soon-badge">Coming Soon</span>}
                  </div>

                  <h3 className="lp-card-title">{m.title}</h3>
                  <p className="lp-card-desc">{m.desc}</p>
                </div>

              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="lp-section lp-section-soft">
        <div className="page-container features-layout">
          <div className="features-left">
            <Reveal>
              <h2>Why Site360 actually helps</h2>
              <p>
                MVP is focused on tracking, accountability, and reporting. No fluff — just features
                that save hours every week.
              </p>
            </Reveal>
          </div>
          <div className="features-right">
            <div className="cards-grid small-gap">
              {features.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.07}>
                  <div className="lp-feature">
                    <h3 className="lp-feature-title">{f.title}</h3>
                    <p className="lp-feature-desc">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="lp-section">
        <div className="page-container">
          <Reveal className="section-header">
            <h2>Simple flow. Real control.</h2>
            <p>Your team understands it in minutes, not weeks.</p>
          </Reveal>

          <div className="steps-grid">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08}>
                <div className="lp-step">
                  <div className="step-num">{s.num}</div>
                  <h3 className="lp-step-title">{s.title}</h3>
                  <p className="lp-step-desc">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI BANNER */}
      <section className="lp-ai-banner">
        <div className="page-container">
          <Reveal className="lp-ai-inner">
            <div className="lp-ai-text">
              <h3>Smart Suggestions (Coming Soon)</h3>
              <p>
                Planned in next phases: reorder alerts, labor overruns, cost spikes, and delay risk warnings —
                after we learn from real MVP usage.
              </p>

            </div>
            <button
              className="btn-hom btn-dark"
              onClick={goRoadmap}
            >
              See Roadmap
            </button>
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lp-section">
        <div className="page-container">
          <Reveal className="section-header">
            <h2>Pricing that scales with you</h2>
            <p>Start free. Upgrade only when you see ROI.</p>
          </Reveal>

          <div className="pricing-grid">
            {plans.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div className={`lp-price-card ${p.popular ? 'popular' : ''}`}>
                  {p.popular && <div className="popular-badge">Most Popular</div>}
                  <h3 className="lp-price-name">{p.name}</h3>
                  <div className="lp-price-value">{p.price}</div>
                  <p className="lp-price-tagline">{p.tagline}</p>
                  <ul className="lp-price-list">
                    {p.items.map((it) => (
                      <li key={it}>{it}</li>
                    ))}
                  </ul>
                  <button
                    className={`btn w-100 ${p.popular ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => {
                      if (p.name.toLowerCase() === 'pro') goToBilling();
                      else if (p.name.toLowerCase() === 'free') goStartFree();
                      else scrollToId('contact'); // enterprise -> talk to sales
                    }}
                  >
                    {p.cta}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="lp-section lp-section-soft">
        <div className="page-container">
          <Reveal className="section-header">
            <h2>What early users feel</h2>
            <p>Real feedback from people close to site work.</p>
          </Reveal>

          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <Reveal key={t.role} delay={i * 0.08}>
                <div className="lp-quote">
                  <div className="quote-mark">“</div>
                  <p className="lp-quote-text">{t.text}</p>
                  <div className="lp-quote-role">{t.role}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="lp-section">
        <div className="page-container">
          <Reveal className="section-header">
            <h2>FAQ</h2>
            <p>Answering obvious questions before they waste your time.</p>
          </Reveal>

          <div className="faq-list">
            {faqs.map((f, index) => {
              const open = openFaqIndex === index;
              return (
                <div
                  key={f.q}
                  className={`faq-item ${open ? 'open' : ''}`}
                  onClick={() => setOpenFaqIndex(open ? -1 : index)}
                >
                  <div className="faq-question-row">
                    <h3 className="faq-question">{f.q}</h3>
                    <span className="faq-toggle">{open ? '-' : '+'}</span>
                  </div>
                  {open && <p className="faq-answer">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA + FOOTER */}
      <section id="contact" className="lp-final-cta">
        <div className="page-container">
          <Reveal className="lp-final-inner">
            <h2 className="final-title">Ready to run your sites smarter?</h2>
            <p className="final-subtitle">
              Start with one project. If it doesn’t save time and money, don’t upgrade. Simple.
            </p>
            <div className="final-cta-row">
              <button className="btn-hom btn-light" onClick={goCreateAccount}>
                Create Account
              </button>
              <button
                onClick={goSeePlans}
              >
                See Plans
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <FooterWithNewsletter />

    </div>
  );
};

export default Homepage;
